import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject, OnInit } from '@angular/core';
import { HlrRates } from '@helpers/types/models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlarisDialogService, AlarisEditPanelService, EditPanelInputData } from '@campaign-portal/components-library';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RatesService } from '../rates.service';
import { CurrenciesService } from '../../currencies/currencies.service';
import { UserService } from 'src/app/auth/user.service';
import moment from 'moment';
import { RatesEditDialogComponentComponent } from '../rates-edit-dialog-component/rates-edit-dialog-component.component';
import { of, switchMap } from 'rxjs';
import { AdditionalValidators } from '@helpers/shared/validators';

@Component({
	selector: 'app-edit-rate',
	templateUrl: './edit-rates.component.html',
	styleUrl: './edit-rates.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class EditRateComponent implements OnInit {
	readonly errors: { key: string; value: string }[] = [
		{ key: 'mask', value: 'gl.errors.invalid_format' },
		{ key: 'required', value: 'gl.errors.required' }
	];
	readonly rate: HlrRates = {
		id: null,
		currencyId: null,
		currencyName: '',
		startDate: null,
		rate: null
	};
	readonly form = new FormGroup({
		id: new FormControl<number | null>(null),
		currencyId: new FormControl<number | null>(null, [Validators.required]),
		startDate: new FormControl<string | null>(null, [Validators.required]),
		rate: new FormControl<number | null>(null, [Validators.required, AdditionalValidators.greaterThanZeroValidator()])
	});

	readonly separator = ', ';
	private readonly destroyRef = inject(DestroyRef);

	constructor(
		@Inject(EditPanelInputData) private readonly inputData: { rate?: HlrRates },
		public readonly ratesService: RatesService,
		private readonly editPanel: AlarisEditPanelService,
		public readonly currenciesService: CurrenciesService,
		protected readonly user: UserService,
		private readonly dialog: AlarisDialogService
	) {
		if (this.inputData.rate) {
			this.rate = this.inputData.rate;
		}
	}

	ngOnInit(): void {
		this.reset();
	}

	reset(): void {
		const rate = structuredClone(this.rate);
		this.form.reset(rate);
	}

	save(): void {
		const rate = this.getResultRate();
		if (rate.id && this.rate.startDate && moment(this.rate.startDate).isSameOrBefore(moment(), 'day')) {
			this.dialog
				.open<boolean>(RatesEditDialogComponentComponent, { autoFocus: false })
				.closed.pipe(
					takeUntilDestroyed(this.destroyRef),
					switchMap(result => result ? this.ratesService.update(rate) : of())
				)
				.subscribe((resp) => {
					if (resp.data[0]?.id) {
						this.editPanel.close(true);
					}
				});
		} else {
			this.ratesService.update(rate).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
				if (resp.data[0]?.id) {
					this.editPanel.close(true);
				}
			});
		}
	}

	private getResultRate(): HlrRates {
		const start = new Date(this.form.controls.startDate.value as string);
		start.setUTCHours(0, 0, 0, 0);
		const rate = {
			id: this.form.controls.id.value,
			currencyId: this.form.controls.currencyId.value,
			startDate: start.toJSON(),
			rate: this.form.controls.rate.value
		};
		return rate;
	}

	close(): void {
		this.editPanel.close(false);
	}

	displayWithName = (currencyId: number | null): string => {
		if (currencyId) {
			return this.currenciesService.map.get(currencyId)?.name || '';
		}
		return '';
	};

	editForbidden(): boolean {
		const date = this.form.controls.startDate.value;
		if (date && moment(date).isBefore(moment(), 'day')) {
			return !this.user.allowed(['rates.edit.past']);
		}
		return false;
	}
}
